// RL-CAN-26: the browser's side of the paged room-universe transport. A
// large indexed catalog never arrives twice as complete arrays: the room
// reads ONE digest-bound summary (group folds plus selection bitsets, under
// 256 KiB at 20,000 members) and then only the pages it shows — at most 512
// members each, bound to the catalog digest and the selection cut the
// browser holds. Everything here is pure: request keys, response parsing,
// and the bitset fold that marks a page row without a member list.
import {
  decodeSelectionBitset,
  selectionBitsetHas,
  isCanvasContentType,
  type CanvasContentType,
} from "@cleo/contracts";

/** A room whose catalog exceeds this many members reads through pages. */
export const ROOM_UNIVERSE_PAGED_THRESHOLD = 2_048;
/** The page law: no page carries more than this many members. */
export const ROOM_UNIVERSE_PAGE_MAX = 512;

const SHA256_HEX = /^[0-9a-f]{64}$/u;

export interface RoomUniverseGroupSummary {
  readonly group: string;
  readonly state: string;
  readonly total: number;
  readonly selected: number | null;
  readonly digest: string;
  /** world-vfs only: the exact fold per contractual kind (CB16 R3). */
  readonly kinds?: Readonly<
    Record<string, { readonly total: number; readonly selected: number | null }>
  >;
  readonly reason?: string;
  readonly truncatedAt?: number;
}

export interface RoomUniverseSummary {
  readonly groups: ReadonlyArray<RoomUniverseGroupSummary>;
  readonly total: number;
  readonly selected: number | null;
  /** Per group, base64 bitset over the group's ordered members; null without an overlay. */
  readonly bitsets: Readonly<Record<string, string>> | null;
  /** Per group, base64 bitset of members this thread made (SESSION LAW); absent when none was. */
  readonly created?: Readonly<Record<string, string>>;
}

export interface RoomUniversePageFilter {
  /** Exact candidate identities; the room catalog remains the ceiling. */
  readonly memberIds?: ReadonlyArray<string>;
  readonly text?: string;
  readonly scope?: "all" | "in" | "out";
  readonly kind?:
    | "canvas"
    | "blueprint"
    | "board-component"
    | "other"
    | "media"
    | "history"
    | "crm"
    | "contacts"
    | "mobile-app"
    | "files";
  readonly under?: string;
}

export interface RoomUniversePageMember {
  readonly contentType?: CanvasContentType;
  readonly delivery?: "include" | "reference";
  readonly id: string;
  readonly label: string;
  readonly index: number;
  readonly inScope: boolean | null;
  /** SESSION LAW: this thread made it; the rail says "Made in this session". */
  readonly created?: true;
}

export interface RoomUniversePage {
  /** Corpus pages have bounded continuations, not a preloaded global count. */
  readonly nextCursor?: string | null;
  readonly totalIsExact?: boolean;
  /** Members the World sent that this Desk could not show (left out, not failed). */
  readonly skipped?: number;
  readonly group: string;
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
  readonly selected: number | null;
  readonly members: ReadonlyArray<RoomUniversePageMember>;
}

export interface RoomUniversePageRequest {
  readonly refresh?: boolean | undefined;
  readonly directory?: string | undefined;
  readonly cursor?: string | undefined;
  readonly group: string;
  readonly filter?: RoomUniversePageFilter | undefined;
  readonly offset?: number | undefined;
}

export type RoomUniversePageState =
  | { readonly state: "loading" }
  | { readonly state: "refused"; readonly reason: string }
  | { readonly state: "page"; readonly page: RoomUniversePage };

export interface RoomUniverseSelectionPredicate {
  readonly group: string;
  readonly filter?: RoomUniversePageFilter | undefined;
  readonly gesture: "tri" | "in" | "out";
}

/** A filter with no live field is no filter — the same key as none. */
export function normalizePageFilter(
  filter: RoomUniversePageFilter | undefined,
): RoomUniversePageFilter | undefined {
  if (filter === undefined) return undefined;
  const normalized: {
    memberIds?: ReadonlyArray<string>;
    text?: string;
    scope?: "all" | "in" | "out";
    kind?: NonNullable<RoomUniversePageFilter["kind"]>;
    under?: string;
  } = {};
  if (filter.memberIds !== undefined) normalized.memberIds = [...new Set(filter.memberIds)].sort();
  const text = filter.text?.trim();
  if (text !== undefined && text !== "") normalized.text = text;
  if (filter.scope !== undefined && filter.scope !== "all") normalized.scope = filter.scope;
  if (filter.kind !== undefined) normalized.kind = filter.kind;
  if (filter.under !== undefined && filter.under !== "") normalized.under = filter.under;
  return Object.keys(normalized).length === 0 ? undefined : normalized;
}

/** The one key a page request answers to — group, normalized filter, offset. */
export function roomUniversePageKey(request: RoomUniversePageRequest): string {
  const filter = normalizePageFilter(request.filter);
  return JSON.stringify([
    request.group,
    filter === undefined
      ? null
      : [
          filter.text ?? null,
          filter.scope ?? null,
          filter.kind ?? null,
          filter.under ?? null,
          ...(filter.memberIds === undefined ? [] : [filter.memberIds]),
        ],
    request.offset ?? 0,
    ...(request.cursor === undefined ? [] : [request.cursor]),
  ]);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

export function parseRoomUniverseSummary(value: unknown): RoomUniverseSummary | null {
  if (!isRecord(value) || !Array.isArray(value["groups"]) || !isCount(value["total"])) {
    return null;
  }
  const selected = value["selected"];
  if (selected !== null && !isCount(selected)) return null;
  const bitsets = value["bitsets"];
  if (bitsets !== null && bitsets !== undefined && !isRecord(bitsets)) return null;
  const groups: RoomUniverseGroupSummary[] = [];
  for (const raw of value["groups"]) {
    if (
      !isRecord(raw) ||
      typeof raw["group"] !== "string" ||
      typeof raw["state"] !== "string" ||
      !isCount(raw["total"]) ||
      (raw["selected"] !== null && !isCount(raw["selected"])) ||
      typeof raw["digest"] !== "string" ||
      !SHA256_HEX.test(raw["digest"])
    ) {
      return null;
    }
    const kinds: Record<string, { total: number; selected: number | null }> = {};
    if (raw["kinds"] !== undefined) {
      if (!isRecord(raw["kinds"])) return null;
      for (const [kind, fold] of Object.entries(raw["kinds"])) {
        if (
          !isRecord(fold) ||
          !isCount(fold["total"]) ||
          (fold["selected"] !== null && !isCount(fold["selected"]))
        ) {
          return null;
        }
        kinds[kind] = { total: fold["total"], selected: fold["selected"] as number | null };
      }
    }
    groups.push({
      group: raw["group"],
      state: raw["state"],
      total: raw["total"],
      selected: raw["selected"] as number | null,
      digest: raw["digest"],
      ...(raw["kinds"] === undefined ? {} : { kinds }),
      ...(typeof raw["reason"] === "string" ? { reason: raw["reason"] } : {}),
      ...(isCount(raw["truncatedAt"]) ? { truncatedAt: raw["truncatedAt"] } : {}),
    });
  }
  const parsedBitsets: Record<string, string> = {};
  if (isRecord(bitsets)) {
    for (const [group, encoded] of Object.entries(bitsets)) {
      if (typeof encoded !== "string") return null;
      parsedBitsets[group] = encoded;
    }
  }
  const created = value["created"];
  if (created !== undefined && !isRecord(created)) return null;
  const parsedCreated: Record<string, string> = {};
  if (isRecord(created)) {
    for (const [group, encoded] of Object.entries(created)) {
      if (typeof encoded !== "string") return null;
      parsedCreated[group] = encoded;
    }
  }
  return {
    groups,
    total: value["total"],
    selected: selected as number | null,
    bitsets: bitsets === null || bitsets === undefined ? null : parsedBitsets,
    ...(isRecord(created) ? { created: parsedCreated } : {}),
  };
}

export function parseRoomUniversePage(value: unknown): RoomUniversePage | null {
  if (
    !isRecord(value) ||
    typeof value["group"] !== "string" ||
    !isCount(value["offset"]) ||
    !isCount(value["limit"]) ||
    value["limit"] > ROOM_UNIVERSE_PAGE_MAX ||
    !isCount(value["total"]) ||
    (value["selected"] !== null && !isCount(value["selected"])) ||
    (value["nextCursor"] !== undefined &&
      value["nextCursor"] !== null &&
      (typeof value["nextCursor"] !== "string" || value["nextCursor"].length > 16384)) ||
    (value["totalIsExact"] !== undefined && typeof value["totalIsExact"] !== "boolean") ||
    !Array.isArray(value["members"]) ||
    value["members"].length > ROOM_UNIVERSE_PAGE_MAX
  ) {
    return null;
  }
  const members: RoomUniversePageMember[] = [];
  for (const raw of value["members"]) {
    if (
      !isRecord(raw) ||
      typeof raw["id"] !== "string" ||
      typeof raw["label"] !== "string" ||
      !isCount(raw["index"]) ||
      (raw["inScope"] !== null && typeof raw["inScope"] !== "boolean")
    ) {
      return null;
    }
    members.push({
      id: raw["id"],
      label: raw["label"],
      ...(isCanvasContentType(raw["contentType"]) ? { contentType: raw["contentType"] } : {}),
      index: raw["index"],
      inScope: raw["inScope"] as boolean | null,
      ...(raw["delivery"] === "include" || raw["delivery"] === "reference"
        ? { delivery: raw["delivery"] }
        : {}),
      ...(raw["created"] === true ? { created: true as const } : {}),
    });
  }
  return {
    ...(value["nextCursor"] === null || typeof value["nextCursor"] === "string"
      ? { nextCursor: value["nextCursor"] }
      : {}),
    ...(typeof value["totalIsExact"] === "boolean" ? { totalIsExact: value["totalIsExact"] } : {}),
    ...(isCount(value["skipped"]) && value["skipped"] > 0 ? { skipped: value["skipped"] } : {}),
    group: value["group"],
    offset: value["offset"],
    limit: value["limit"],
    total: value["total"],
    selected: value["selected"] as number | null,
    members,
  };
}

/**
 * The mark of one catalog position folded from the summary's bitset — the
 * same truth a page row carries, readable without any page. Null without an
 * overlay (no bitsets) or for a group the summary did not mark.
 */
export function summaryMemberInScope(
  summary: RoomUniverseSummary,
  group: string,
  index: number,
): boolean | null {
  const encoded = summary.bitsets?.[group];
  if (encoded === undefined) return null;
  return (
    selectionBitsetHas(decodeSelectionBitset(encoded), index) ||
    summaryMemberCreated(summary, group, index)
  );
}

/** Whether the group's i-th member was made in this session, from the summary's created bitset. */
export function summaryMemberCreated(
  summary: RoomUniverseSummary,
  group: string,
  index: number,
): boolean {
  const encoded = summary.created?.[group];
  if (encoded === undefined) return false;
  return selectionBitsetHas(decodeSelectionBitset(encoded), index);
}
